import { useState, useEffect, useTransition, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building2,
  Calendar,
  Users,
  FlaskConical,
  FlaskConicalOff,
  Loader2,
} from "lucide-react";
import { adminOrganizationsService } from "@/services/adminOrganizationsService";
import {
  SkeletonTable,
  SkeletonText,
  SkeletonCard,
} from "@/components/ui/skeleton";

type SortField =
  | "name"
  | "created_at"
  | "subscription_status"
  | "active_agents_count"
  | "industry"
  | "plan";
type SortDirection = "asc" | "desc" | null;

// Helper function to generate avatar background colors
const getAvatarColor = (name: string) => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-teal-500",
  ];

  if (!name) return colors[0];

  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

// Helper function to get organization initials
const getOrganizationInitials = (name: string) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Helper function to get subscription status display
const getSubscriptionStatusDisplay = (status?: string) => {
  const statusLower = (status || "free").toLowerCase();
  switch (statusLower) {
    case "active":
      return {
        text: "Active",
        variant: "default" as const,
        className: "bg-green-100 text-green-800 border-green-200",
      };
    case "trialing":
      return {
        text: "Trial",
        variant: "secondary" as const,
        className: "bg-blue-100 text-blue-800 border-blue-200",
      };
    case "past_due":
      return {
        text: "Past Due",
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800 border-red-200",
      };
    case "canceled":
      return {
        text: "Canceled",
        variant: "secondary" as const,
        className: "bg-gray-100 text-gray-800 border-gray-200",
      };
    default:
      return {
        text: "Free",
        variant: "secondary" as const,
        className: "bg-gray-100 text-gray-600 border-gray-200",
      };
  }
};

// Helper function to get onboarding stage
const getOnboardingStage = (meta?: any) => {
  if (!meta) return "Not Started";
  const { completed_at } = meta;
  if (completed_at) return "Completed";
  return "In Progress";
};

// Helper to format date in Paris time like: Aug 20 - 10:01pm
const formatParisDateTime = (isoString: string | null) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    const monthDay = date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "Europe/Paris",
    });
    const time = date
      .toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "Europe/Paris",
      })
      .replace(" ", "")
      .toLowerCase();
    return `${monthDay} - ${time}`;
  } catch {
    return "-";
  }
};

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // React 18.3: useTransition for non-blocking URL updates
  const [isPending, startTransition] = useTransition();

  // Local state for filters (synced with URL)
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>("all");
  const [onboardingFilter, setOnboardingFilter] = useState<string>("all");
  const [createdAfter, setCreatedAfter] = useState<string>("");
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 50;

  // TanStack Query: Fetch organizations with server-side pagination/filtering/sorting
  const {
    data: organizationsResponse,
    isLoading,
    isFetching,
    error: organizationsError,
  } = useQuery({
    queryKey: [
      "admin-organizations",
      {
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
        status: subscriptionFilter !== "all" ? subscriptionFilter : undefined,
        onboarding: onboardingFilter !== "all" ? onboardingFilter : undefined,
        createdAfter: createdAfter || undefined,
        sortField: sortField || undefined,
        sortDirection: sortDirection || undefined,
      },
    ],
    queryFn: () =>
      adminOrganizationsService.getOrganizations({
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
        status: subscriptionFilter !== "all" ? subscriptionFilter : undefined,
        onboarding: onboardingFilter !== "all" ? onboardingFilter : undefined,
        createdAfter: createdAfter || undefined,
        sortField: sortField || undefined,
        sortDirection: sortDirection || undefined,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes - matches server cache
    placeholderData: (previousData) => previousData, // Keep previous data while fetching (prevents full UI reload)
  });

  // TanStack Query: Fetch dashboard metrics
  const { data: metrics } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: () => adminOrganizationsService.getMetrics(),
    staleTime: 5 * 60 * 1000, // 5 minutes - matches server cache
  });

  // TanStack Query: Mutation for updating organization
  const updateOrgMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<{ is_test_org: boolean }>;
    }) => adminOrganizationsService.updateOrganization(id, updates),
    onSuccess: () => {
      // Invalidate and refetch organizations list
      queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update organization. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Show error toast if organizations query fails
  useEffect(() => {
    if (organizationsError) {
      toast({
        title: "Error",
        description: "Failed to load organizations. Please try again.",
        variant: "destructive",
      });
    }
  }, [organizationsError, toast]);

  // Initialize state from URL params on first render
  useEffect(() => {
    const qp = Object.fromEntries(searchParams.entries());
    if (qp.q !== undefined) {
      setSearchTerm(qp.q);
      setDebouncedSearchTerm(qp.q);
    }
    if (qp.status) setSubscriptionFilter(qp.status);
    if (qp.onboarding) setOnboardingFilter(qp.onboarding);
    if (qp.createdAfter) setCreatedAfter(qp.createdAfter);
    if (qp.sort) {
      const sf = qp.sort as SortField;
      if (
        [
          "name",
          "created_at",
          "subscription_status",
          "active_agents_count",
          "industry",
          "plan",
        ].includes(sf)
      ) {
        setSortField(sf);
      }
    }
    if (qp.dir) {
      const dir =
        qp.dir === "asc" || qp.dir === "desc"
          ? (qp.dir as SortDirection)
          : null;
      setSortDirection(dir);
    }
    if (qp.page) {
      const n = parseInt(qp.page, 10);
      if (!isNaN(n) && n > 0) setCurrentPage(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input updates
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // React 18.3: Sync state to URL parameters as non-urgent update (prevents UI refresh)
  useEffect(() => {
    const qp = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string | null | undefined) => {
      if (value && value !== "" && value !== "all") qp.set(key, value);
      else qp.delete(key);
    };
    setOrDelete("q", debouncedSearchTerm);
    setOrDelete("status", subscriptionFilter);
    setOrDelete("onboarding", onboardingFilter);
    setOrDelete("createdAfter", createdAfter);
    if (sortField && sortDirection) {
      qp.set("sort", sortField);
      qp.set("dir", sortDirection);
    } else {
      qp.delete("sort");
      qp.delete("dir");
    }
    qp.set("page", String(currentPage));

    // Mark URL update as non-urgent to keep UI responsive
    startTransition(() => {
      setSearchParams(qp, { replace: true });
    });
  }, [
    debouncedSearchTerm,
    subscriptionFilter,
    onboardingFilter,
    createdAfter,
    sortField,
    sortDirection,
    currentPage,
    setSearchParams,
    searchParams,
    startTransition,
  ]);

  // React 18.3: Memoize callbacks to prevent unnecessary re-renders
  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        // Cycle through: asc -> desc -> null
        if (sortDirection === "asc") {
          setSortDirection("desc");
        } else if (sortDirection === "desc") {
          setSortField(null);
          setSortDirection(null);
        } else {
          setSortDirection("asc");
        }
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    },
    [sortField, sortDirection],
  );

  const getSortIcon = useCallback(
    (field: SortField) => {
      if (sortField !== field)
        return <ArrowUpDown className="h-4 w-4" style={{ color: "#667085" }} />;
      if (sortDirection === "asc")
        return <ArrowUp className="h-4 w-4" style={{ color: "#667085" }} />;
      if (sortDirection === "desc")
        return <ArrowDown className="h-4 w-4" style={{ color: "#667085" }} />;
      return <ArrowUpDown className="h-4 w-4" style={{ color: "#667085" }} />;
    },
    [sortField, sortDirection],
  );

  // Extract data from TanStack Query response
  const organizations = organizationsResponse?.data || [];
  const pagination = organizationsResponse?.pagination || {
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 1,
  };

  // Reset pagination to first page when filters/sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    subscriptionFilter,
    onboardingFilter,
    createdAfter,
    sortField,
    sortDirection,
  ]);

  // Use server-provided pagination info
  const totalItems = pagination.total;
  const totalPages = pagination.totalPages;
  const startIdx = (pagination.page - 1) * pagination.pageSize;
  const endIdx = Math.min(startIdx + organizations.length, totalItems);

  if (isLoading) {
    return (
      <MainLayout>
        <SuperAdminLayout title="Organizations" fullWidth>
          <div className="flex flex-col h-full">
            {/* Filters Section Skeleton (production-grade) */}
            <div className="flex-shrink-0 p-6 pb-0">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
                  <SkeletonText className="w-full sm:w-64" height="md" />
                  <SkeletonText className="w-36" height="md" />
                  <SkeletonText className="w-40" height="md" />
                  <SkeletonText className="w-44" height="md" />
                </div>
                <SkeletonText className="w-28" height="sm" />
              </div>
            </div>

            {/* Metrics Cards Skeleton */}
            <div className="px-6 pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <SkeletonCard padding="lg" />
              <SkeletonCard padding="lg" />
              <SkeletonCard padding="lg" />
            </div>

            {/* Table Skeleton */}
            <div className="flex-1 p-6 pt-6">
              <SkeletonTable
                rows={8}
                columns={11}
                showHeader={true}
                showAvatar={true}
                avatarColumn={0}
              />
            </div>
          </div>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SuperAdminLayout title="Organizations" fullWidth>
        {/* React 18.3: Transition loading indicator */}
        {isPending && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg shadow-sm z-50">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">Updating...</span>
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Filters Section - Fixed at top */}
          <div className="flex-shrink-0 p-6 pb-0 bg-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Filters and Search - Left side on desktop, stacked on mobile */}
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
                {/* Search */}
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full bg-white border-gray-300"
                  />
                </div>

                {/* Subscription Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <Select
                    value={subscriptionFilter}
                    onValueChange={setSubscriptionFilter}
                  >
                    <SelectTrigger className="w-[140px] bg-white border-gray-300">
                      <SelectValue placeholder="Subscription" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trialing">Trial</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="past_due">Past Due</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Onboarding Filter */}
                <Select
                  value={onboardingFilter}
                  onValueChange={setOnboardingFilter}
                >
                  <SelectTrigger className="w-[140px] bg-white border-gray-300">
                    <SelectValue placeholder="Onboarding" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                  </SelectContent>
                </Select>
                {/* Created After Date Filter */}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <Input
                    type="date"
                    value={createdAfter}
                    onChange={(e) => setCreatedAfter(e.target.value)}
                    className="w-[170px] bg-white border-gray-300"
                  />
                </div>
              </div>

              {/* Results Count + Pagination - Right side on desktop, below on mobile */}
              <div className="flex items-center justify-end lg:justify-start gap-3">
                <p className="text-sm text-gray-600 whitespace-nowrap">
                  Showing {totalItems === 0 ? 0 : startIdx + 1}-{endIdx} of{" "}
                  {totalItems}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="h-8 px-2"
                  >
                    Prev
                  </Button>
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    Page {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className="h-8 px-2"
                  >
                    Next
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 px-3 ml-2"
                    onClick={() => {
                      const rows = organizations;
                      const esc = (v: any) => {
                        if (v === null || v === undefined) return "";
                        const s = String(v);
                        const needsQuotes = /[",\n]/.test(s);
                        const escaped = s.replace(/"/g, '""');
                        return needsQuotes ? `"${escaped}"` : escaped;
                      };
                      const headers = [
                        "id",
                        "name",
                        "created_at",
                        "website",
                        "subscription_status",
                        "plan_name",
                        "industry_name",
                        "active_agents_count",
                        "ai_outputs_count",
                        "ai_assistant_instances_count",
                      ];
                      const lines = [headers.join(",")];
                      for (const r of rows) {
                        lines.push(
                          [
                            esc(r.id),
                            esc(r.name),
                            esc(r.created_at || ""),
                            esc(r.website || ""),
                            esc(r.subscription_status || "free"),
                            esc(r.plan_name || ""),
                            esc(r.industry_name || ""),
                            esc(r.active_agents_count ?? 0),
                            esc(r.ai_outputs_count ?? 0),
                            esc(r.ai_assistant_instances_count ?? 0),
                          ].join(","),
                        );
                      }
                      const blob = new Blob([lines.join("\n")], {
                        type: "text/csv;charset=utf-8;",
                      });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      const ts = new Date().toISOString().replace(/[:.]/g, "-");
                      a.download = `organizations-${ts}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Export CSV
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="px-6 pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">
                  New signups
                </CardTitle>
                <CardDescription>Organizations created</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-xs text-gray-500">Today</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.new_signups_today ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Yesterday</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.new_signups_yesterday ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Last 7 days</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.new_signups_last7 ?? 0}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">
                  Signup to completed
                </CardTitle>
                <CardDescription>
                  Orgs that completed onboarding
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-xs text-gray-500">Today</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.completed_today ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Yesterday</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.completed_yesterday ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Last 7 days</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.completed_last7 ?? 0}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">
                  AI assistant generations
                </CardTitle>
                <CardDescription>
                  Excludes super admin & test orgs
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-xs text-gray-500">Today</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.outputs_today ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Yesterday</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.outputs_yesterday ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Last 7 days</div>
                  <div className="text-2xl font-semibold">
                    {metrics?.outputs_last7 ?? 0}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Scrollable Table Container */}
          <div className="flex-1 p-6 pt-6 overflow-hidden">
            <div className="bg-white rounded-lg h-full flex flex-col relative">
              {/* Loading overlay - only shows during refetch, not initial load */}
              {!isLoading && isFetching && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-40 rounded-lg">
                  <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-lg border border-gray-200">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Loading results...
                    </span>
                  </div>
                </div>
              )}

              {/* Scrollable Table with Fixed Header */}
              <div className="flex-1 overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-50 z-10">
                    <TableRow>
                      <TableHead className="min-w-[250px] text-xs font-medium text-[#667085] py-4">
                        Organization
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort("created_at")}
                          className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                        >
                          Sign Up Date
                          <span className="ml-1 text-[#667085]">
                            {getSortIcon("created_at")}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-[200px] text-xs font-medium text-[#667085]">
                        User Details
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort("industry")}
                          className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                        >
                          Industry
                          <span className="ml-1 text-[#667085]">
                            {getSortIcon("industry")}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-[150px] text-xs font-medium text-[#667085] hidden lg:table-cell">
                        Website
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        Onboarding
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort("subscription_status")}
                          className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                        >
                          Subscription
                          <span className="ml-1 text-[#667085]">
                            {getSortIcon("subscription_status")}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-[100px] text-xs font-medium text-[#667085]">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort("active_agents_count")}
                          className="h-auto p-0 text-xs font-medium text-[#667085] hover:bg-transparent hover:text-gray-900"
                        >
                          Agents
                          <span className="ml-1 text-[#667085]">
                            {getSortIcon("active_agents_count")}
                          </span>
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        AI Outputs
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        AI Assistants
                      </TableHead>
                      <TableHead className="min-w-[120px] text-xs font-medium text-[#667085]">
                        Phone Agents
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map((org) => {
                      const subscriptionDisplay = getSubscriptionStatusDisplay(
                        org.subscription_status,
                      );
                      const onboardingStage = getOnboardingStage(
                        org.onboarding_meta,
                      );
                      const userDisplayName =
                        org.profile_full_name || org.profile_email || "Unknown";

                      return (
                        <TableRow
                          key={org.id}
                          className="hover:bg-gray-50 border-b border-gray-200 cursor-pointer"
                          onClick={() =>
                            navigate(`/super-admin/organizations/${org.id}`)
                          }
                        >
                          {/* Organization Column */}
                          <TableCell className="py-4">
                            <div className="flex items-center gap-3">
                              {/* Organization Logo or Initials */}
                              <div className="flex-shrink-0">
                                {org.logo_url ? (
                                  <img
                                    src={org.logo_url}
                                    alt={org.name}
                                    className="w-10 h-10 rounded-lg object-cover"
                                  />
                                ) : (
                                  <div
                                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(org.name)}`}
                                  >
                                    {getOrganizationInitials(org.name)}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {org.name}
                                </span>
                                <button
                                  className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700"
                                  title={
                                    org.is_test_org
                                      ? "Unmark test org"
                                      : "Mark as test org"
                                  }
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    updateOrgMutation.mutate({
                                      id: org.id,
                                      updates: {
                                        is_test_org: !org.is_test_org,
                                      },
                                    });
                                  }}
                                >
                                  {org.is_test_org ? (
                                    <FlaskConical className="h-4 w-4" />
                                  ) : (
                                    <FlaskConicalOff className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </TableCell>

                          {/* Sign Up Date Column */}
                          <TableCell className="py-4">
                            <div className="text-sm text-gray-600">
                              {formatParisDateTime(org.created_at)}
                            </div>
                          </TableCell>

                          {/* User Details Column */}
                          <TableCell className="py-4">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900">
                                {userDisplayName}
                              </span>
                              <span className="text-sm text-gray-500">
                                {org.profile_email || "-"}
                              </span>
                            </div>
                          </TableCell>

                          {/* Industry Column */}
                          <TableCell className="py-4">
                            <div className="text-sm text-gray-700">
                              {org.industry_name ? (
                                <span>
                                  {org.industry_emoji &&
                                    `${org.industry_emoji} `}
                                  {org.industry_name}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </TableCell>

                          {/* Website Column - Hidden on mobile */}
                          <TableCell className="py-4 hidden lg:table-cell">
                            <div className="text-sm text-gray-700 max-w-[150px] truncate">
                              {org.website ? (
                                <a
                                  href={
                                    org.website.startsWith("http")
                                      ? org.website
                                      : `https://${org.website}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {org.website.replace(/^https?:\/\//, "")}
                                </a>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                          </TableCell>

                          {/* Onboarding Column */}
                          <TableCell className="py-4">
                            <Badge
                              variant={
                                onboardingStage === "Completed"
                                  ? "default"
                                  : "secondary"
                              }
                              className={
                                onboardingStage === "Completed"
                                  ? "bg-green-100 text-green-800 border-green-200"
                                  : "bg-yellow-100 text-yellow-800 border-yellow-200"
                              }
                            >
                              {onboardingStage}
                            </Badge>
                          </TableCell>

                          {/* Subscription Column */}
                          <TableCell className="py-4">
                            <div className="flex flex-col gap-1">
                              <Badge
                                variant={subscriptionDisplay.variant}
                                className={subscriptionDisplay.className}
                              >
                                {subscriptionDisplay.text}
                              </Badge>
                              {org.plan_name && (
                                <span className="text-xs text-gray-500">
                                  {org.plan_name}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Agents Column */}
                          <TableCell className="py-4">
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span className="text-sm font-medium">
                                {org.active_agents_count || 0}
                              </span>
                            </div>
                          </TableCell>
                          {/* AI Outputs Column */}
                          <TableCell className="py-4">
                            <span className="text-sm text-gray-700">
                              {org.ai_outputs_count ?? 0}
                            </span>
                          </TableCell>
                          {/* AI Assistants Column */}
                          <TableCell className="py-4">
                            <span className="text-sm text-gray-700">
                              {(org as any).ai_assistant_instances_count ?? 0}
                            </span>
                          </TableCell>
                          {/* Phone Agents Column */}
                          <TableCell className="py-4">
                            <span className="text-sm text-gray-700">-</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {organizations.length === 0 && !isLoading && (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-12">
                          <div className="flex flex-col items-center gap-2">
                            <Building2 className="h-12 w-12 text-gray-300" />
                            <p className="text-gray-500 text-lg">
                              No organizations found
                            </p>
                            <p className="text-gray-400 text-sm">
                              Try adjusting your search or filter criteria
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default OrganizationsPage;
